import React, { useState } from "react";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AiOutlineSearch } from "react-icons/ai";

const modelToZoneMap = {
  Readaloud: "Speaking",
  RepeatSentence: "Speaking",
  DescribeImage: "Speaking",
  RetellLecture: "Speaking",
  AnswershortQuestion: "Speaking",
  SummarizeSpokenText: "Listening",
  WritefromDictation: "Listening",
  HighlightSummary: "Listening",
  MissingWord: "Listening",
  IncorrectWord: "Listening",
  SummarizeText: "Writing",
  WriteEssay: "Writing",
  SingleAnswer: "Reading",
  DoubleAnswer: "Reading",
  ReorderParagraph: "Reading",
  FillInTheBlank: "Reading",
};

const zoneClass = (zone) => {
  if (zone === "Speaking") return "bg-sky-50 text-sky-700 ring-sky-100";
  if (zone === "Listening") return "bg-indigo-50 text-indigo-700 ring-indigo-100";
  if (zone === "Writing") return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  if (zone === "Reading") return "bg-amber-50 text-amber-700 ring-amber-100";
  return "bg-slate-100 text-slate-600 ring-slate-200";
};

export default function QuestionTable({ questions, currentPage = 1, limit = 10, handleEditClick }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);

  const highlightText = (text, query) => {
    if (!text) return "";
    if (typeof text !== "string") return text;
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, "i"));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={`${part}-${index}`} className="rounded bg-amber-100 px-1 text-amber-800">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const filteredQuestions = questions.filter((question) => {
    const title = typeof question.q_title === "string" ? question.q_title : "";
    const isTitleMatch = title.toLowerCase().includes(searchQuery.toLowerCase());
    const isIdMatch = question._id !== null && question._id !== undefined && question._id.toString().includes(searchQuery);

    return isTitleMatch || isIdMatch;
  });

  const updatedQuestions = filteredQuestions.map((question) => ({
    ...question,
    zone: modelToZoneMap[question.modelName] || "Unknown",
  }));

  const handleDeleteClick = async (id, modelName) => {
    try {
      const response = await fetch(`/api/admin/questions/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ model: modelName.toLowerCase() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete question");
      }

      toast.success(data.message);
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete the question");
    }
  };

  const handleConfirmDelete = () => {
    const questionToDelete = updatedQuestions.find((question) => question._id === currentQuestionId);

    if (!questionToDelete) {
      alert("Question not found. Please refresh the list and try again.");
      setIsModalOpen(false);
      return;
    }

    handleDeleteClick(currentQuestionId, questionToDelete.modelName);
    setIsModalOpen(false);
    setCurrentQuestionId(null);
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Question Table</h2>
          <p className="text-sm text-slate-500">Review question records by zone, section, and ID.</p>
        </div>
        <label className="relative w-full lg:w-96">
          <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
          <input
            type="text"
            placeholder="Search table by title or ID"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
          />
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] text-left text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">S.No</th>
              <th className="px-4 py-3 font-semibold">ID</th>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Zone</th>
              <th className="px-4 py-3 font-semibold">Section</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {updatedQuestions.length > 0 ? (
              updatedQuestions.map((question, index) => {
                const serialNumber = (currentPage - 1) * limit + index + 1;
                return (
                  <tr key={question._id || `${question.id}-${index}`} className="text-slate-700 transition hover:bg-slate-50">
                    <td className="px-4 py-4 font-semibold text-slate-950">{serialNumber}</td>
                    <td className="px-4 py-4 text-slate-500">{question.id}</td>
                    <td className="max-w-md px-4 py-4">
                      <div className="line-clamp-2 font-semibold text-slate-950">
                        {highlightText(question.q_title, searchQuery)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${zoneClass(question.zone)}`}>
                        {question.zone}
                      </span>
                    </td>
                    <td className="px-4 py-4">{question.modelName}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                          onClick={() => handleEditClick(question)}
                          aria-label="Edit question"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                          onClick={() => {
                            setIsModalOpen(true);
                            setCurrentQuestionId(question._id);
                          }}
                          aria-label="Delete question"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="px-4 py-10 text-center text-sm text-slate-500">
                  No questions available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
            <h2 className="text-lg font-bold text-slate-950">Delete question</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Are you sure you want to delete this question? This action will call the admin delete endpoint.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
    </section>
  );
}
